import { SITE } from './constants';

export interface PageSeo {
  /** Page title — will be wrapped by SITE.titleTemplate unless `fullTitle` is true */
  title: string;
  description: string;
  /** Path portion of canonical URL, e.g. "/nossa-essencia". Leading slash required. */
  path: string;
  /** If true, use `title` exactly instead of applying the template */
  fullTitle?: boolean;
  /** Open Graph image absolute or site-relative path */
  ogImage?: string;
  /** Block indexing (e.g. 404) */
  noindex?: boolean;
}

/**
 * Per-route SEO config. Keep descriptions ~150 chars for search snippets.
 * All strings are pt-BR since the audience is Brazilian.
 */
export const SEO_CONFIG: Record<string, PageSeo> = {
  home: {
    title: 'ARCOLL TECH',
    description:
      'ARCOLL TECH — Araucária Collection Tech. Conciliação de dívidas com tecnologia e atendimento humanizado. Pessoas no centro de cada decisão.',
    path: '/',
    fullTitle: true,
  },
  nossosDiferenciais: {
    title: 'Nossos Diferenciais',
    description:
      'Por que escolher a ARCOLL TECH: empatia, conexão direta e especialização. SGI certificado ISO 9001, 27001 e 37001.',
    path: '/nossos-diferenciais',
  },
  nossaEssencia: {
    title: 'Nossa Essência',
    description:
      'Nosso jeito de atender — comprometimento que gera inovação, flexibilidade para inovar, simplicidade com inteligência e pessoas no centro.',
    path: '/nossa-essencia',
  },
  comoFunciona: {
    title: 'Como Funciona',
    description:
      'Cada conexão constrói nossa história. A inteligência por trás da sua proposta: análise preditiva e atendimento personalizado.',
    path: '/como-funciona',
  },
  privacidade: {
    title: 'Privacidade',
    description:
      'Segurança & Confiança. Suas informações estão blindadas — política conforme a LGPD e estrutura para conduta de ponta a ponta.',
    path: '/privacidade',
  },
  sejaNossoParceiro: {
    title: 'Seja Nosso Parceiro',
    description:
      'Para Instituições & Empresas — transformamos resultados B2C em valor B2B. Conheça nossa solução inovadora.',
    path: '/seja-nosso-parceiro',
  },
  guiaDireitoTitulares: {
    title: 'Guia de Direito dos Titulares',
    description:
      'Conheça os direitos garantidos pela Lei Geral de Proteção de Dados Pessoais (LGPD nº 13.709/2018) e como exercê-los na ARCOLL TECH.',
    path: '/guia-direito-titulares',
  },
  trabalheConosco: {
    title: 'Trabalhe Conosco',
    description:
      'Faça parte do nosso time. Deixe o seu currículo na ARCOLL TECH — estamos sempre em busca de pessoas com garra e vontade de crescer.',
    path: '/trabalhe-conosco',
  },
  denuncia: {
    title: 'Canal de Denúncia',
    description:
      'Canal de Denúncia da ARCOLL TECH — registre de forma sigilosa condutas que violem nossa ética e compliance. Tratamento confidencial conforme a LGPD.',
    path: '/denuncia',
  },
  termosDeUso: {
    title: 'Termos de Uso',
    description:
      'Termos e condições de uso do site ARCOLL TECH — direitos, responsabilidades e políticas aplicáveis aos visitantes e usuários.',
    path: '/termos-de-uso',
  },
  notFound: {
    title: 'Página não encontrada',
    description: 'A página que você procura não existe ou foi movida.',
    path: '/404',
    noindex: true,
  },
} as const;

/** Build the full, rendered page title (applying SITE.titleTemplate if needed) */
export function buildTitle(seo: PageSeo): string {
  if (seo.fullTitle) return seo.title;
  return SITE.titleTemplate.replace('%s', seo.title);
}

/** Build the absolute canonical URL for a route */
export function buildCanonical(seo: PageSeo): string {
  const base = SITE.url.replace(/\/$/, '');
  const path = seo.path === '/' ? '' : seo.path;
  return `${base}${path}`;
}
